import React from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import CardHeader from "@mui/material/CardHeader";
import { EmptyDetailCardWithLogin } from "../core/EmptyDetailCard";
import { formatConventionDateNoSubmission, formatFullDate, formatLocation } from "@seabrookstudios/pitch2table-core";
import { usePublicEventBySlug } from "@seabrookstudios/pitch2table";
import ArrowBack from "@mui/icons-material/ArrowBack";
import { Link } from "react-router-dom";
import { Path } from "../Path";
import Button from "@mui/material/Button";
import CardActions from "@mui/material/CardActions";
import { addUtmSource } from "../util/add-utm";
import { PublishersForConventionButton } from "./PublishersForConvention";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";

const BackToEventsButton = () => (
  <Button component={Link} to={Path.public().events()} startIcon={<ArrowBack />}>
    Back to Events
  </Button>
);

/**
 * @param {object} props
 * @param {string} props.slug
 */
export const PublicEventDetail = ({ slug }) => {
  const { data: publicEvent, isLoading } = usePublicEventBySlug(slug);

  if (isLoading) {
    return (
      <Card elevation={1} square={false}>
        <Box p={2} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      </Card>
    );
  }

  if (!publicEvent) {
    return (
      <EmptyDetailCardWithLogin text="Event not found. Please check with the event organizer whether this event is publicly accessible." />
    );
  }

  // Cast to CalendarEvent for formatting functions
  const event = /** @type {import("@seabrookstudios/pitch2table-core").CalendarEvent} */ (publicEvent);

  return (
    <Box width={960} maxWidth="90vw" m="auto">
      <Card elevation={1} square={false}>
        <CardHeader avatar={<BackToEventsButton />} />
        <CardContent>
          <CardMedia component="img" image={publicEvent.logoUrl} alt={publicEvent.name} />
          <CardHeader title={publicEvent.name} />
          <List>
            <ListItem>
              <ListItemText primary="Where" secondary={formatLocation(event)} />
            </ListItem>
            <ListItem>
              <ListItemText primary="When" secondary={formatConventionDateNoSubmission(event)} />
            </ListItem>
            {!!publicEvent.submissionDate && (
              <ListItem>
                <ListItemText primary="Submissions Close" secondary={formatFullDate(publicEvent.submissionDate)} />
              </ListItem>
            )}
            {publicEvent.website && (
              <ListItem>
                <ListItemText
                  primary="Website"
                  secondary={
                    <a href={addUtmSource(publicEvent.website)} target="_blank" rel="noopener noreferrer">
                      {publicEvent.website}
                    </a>
                  }
                />
              </ListItem>
            )}
            {(event.eventOpportunities || []).length > 0 && (
              <ListItem>
                <ListItemText primary="Event Opportunities" secondary={event.eventOpportunities.join(", ")} />
              </ListItem>
            )}
          </List>
        </CardContent>
        {event.numberOfPublishersAttending > 0 && (
          <CardActions>
            <PublishersForConventionButton
              conventionId={publicEvent.id}
              numberOfPublishersAttending={event.numberOfPublishersAttending}
            />
          </CardActions>
        )}
      </Card>
    </Box>
  );
};
